import React, { useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import _isEqual from 'lodash/isEqual';
import { Job, JobStatusEnum, useJobsLazyQuery } from 'graphql-common';
import { JOB_TRACKER_OPENED } from '@lib/enums/localStorageKeys';
import usePrevious from '@lib/hooks/usePrevious';
import handleJobCompletion from 'components/JobTracker/utils/handleJobCompletion';
import useJobsIndexedDB from 'hooks/useJobsIndexedDB';
import {
  clearAllJobsFromIndexedDB,
  removeJobFromDB,
  setOrReplaceJobInDB,
} from 'utils/indexedDBUtils';
import { EventBusEvents, jobEventBus } from '@lib/utils/eventBus';
import { useTabActivity } from '@lib/hooks/useTabActivity';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import fetchJobs from './utils/fetchJobs';
import { POLL_INTERVAL } from './constants';
import DraggableWrapper from './DraggableWrapper';

export default function JobTracker() {
  const [lastUpdate, setLastUpdate] = useState<string>(
    getFormattedDate(new Date()) || '',
  );
  const prevUpdate = usePrevious(lastUpdate);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isAccordionOpen, setAccordionOpen] = useLocalStorage(
    JOB_TRACKER_OPENED,
    true,
  );
  const [fetchJobStatus, { stopPolling, startPolling, called }] =
    useJobsLazyQuery({
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      pollInterval: POLL_INTERVAL,
    });
  const { data, refetch: refetchJobsIds } = useJobsIndexedDB({
    reset: !_isEqual(lastUpdate, prevUpdate),
  });
  const jobIds = data.map(({ id }) => id);
  const prevJobIds = usePrevious(jobIds);
  const isJobIdsChanged =
    !_isEqual(jobIds, prevJobIds) && prevJobIds !== undefined;
  const downloadedJobIds = data
    .filter(({ downloaded }) => downloaded)
    .map(({ id }) => id);

  const onUpdateJobs = useCallback(
    (newJobs: Job[]) => {
      console.log('✊ Update jobs', newJobs, jobIds);
      jobIds.forEach((jobId) => {
        if (!newJobs.some((job) => job.jid === jobId)) {
          removeJobFromDB(jobId);
        }
      });
      setJobs(newJobs);
    },
    [jobIds],
  );

  const toggleAccordion = () => setAccordionOpen((prevState) => !prevState);

  const onRemoveJobHandler = (removedId: string) => {
    console.log(`✊ Job id ${removedId} is removed`);
    removeJobFromDB(removedId).then(() => {
      setJobs((prevState) => prevState.filter(({ jid }) => jid !== removedId));
      refetchJobsIds();
    });
  };

  const onRemoveAllJobIds = () => {
    console.log('✊ All job ids are removed');
    clearAllJobsFromIndexedDB().then(() => {
      setJobs([]);
      refetchJobsIds().then(() => {
        setAccordionOpen(true);
      });
    });
  };

  const restart = () => {
    startPolling(POLL_INTERVAL);
  };

  useEffect(() => {
    if (isJobIdsChanged) {
      console.log('✊ Job Ids Changed; refetch jobs');
      fetchJobs({ jobIds, fetchJobStatus, onUpdateJobs });
    }
  }, [jobIds, isJobIdsChanged, fetchJobStatus, onUpdateJobs]);

  useEffect(() => {
    if (
      isJobIdsChanged &&
      prevJobIds?.length &&
      jobIds.length === 0 &&
      called
    ) {
      console.log('✅ No jobs; Stop Polling');
      stopPolling();
    }
  }, [called, isJobIdsChanged, jobIds, prevJobIds?.length, stopPolling]);

  useEffect(() => {
    if (
      isJobIdsChanged &&
      prevJobIds?.length &&
      prevJobIds.length < jobIds.length
    ) {
      setAccordionOpen(true);
    }
  }, [isJobIdsChanged, jobIds, prevJobIds, setAccordionOpen]);

  useEffect(() => {
    jobs.forEach((job) => {
      if (job?.jid && jobIds.includes(job.jid)) {
        handleJobCompletion(job, downloadedJobIds, setOrReplaceJobInDB);
      }
    });
  }, [jobs, downloadedJobIds, jobIds]);

  useEffect(() => {
    const allJobsAreFinished = jobs.length
      ? jobs.every(
          ({ status }) =>
            status === JobStatusEnum.Failed ||
            status === JobStatusEnum.Stopped ||
            status === JobStatusEnum.Complete ||
            status === JobStatusEnum.Interrupted,
        )
      : false;
    if (allJobsAreFinished) {
      console.log('✅ All jobs are finished; Stop Polling');
      stopPolling();
    }
  }, [jobs, stopPolling]);

  useEffect(() => {
    const handler = () => refetchJobsIds();
    jobEventBus.on(EventBusEvents.JobsUpdated, handler);
    return () => {
      jobEventBus.off(EventBusEvents.JobsUpdated, handler);
    };
  }, [refetchJobsIds]);

  useTabActivity({
    onFocus: () => {
      const newDate = getFormattedDate(new Date());
      if (newDate) setLastUpdate(newDate);
      refetchJobsIds().then(() => {
        if (jobIds.length) restart();
      });
    },
    onBlur: () => {
      if (called && jobIds?.length > 0) {
        console.log('✅ Stop Polling on window blur');
        stopPolling();
      }
    },
  });

  if (!jobs?.length || !jobIds?.length) return null;

  return (
    <DraggableWrapper
      jobs={jobs}
      onRemoveJobHandler={onRemoveJobHandler}
      isAccordionOpen={isAccordionOpen}
      toggleAccordion={toggleAccordion}
      restart={restart}
      stop={stopPolling}
      onRemoveAllJobIds={onRemoveAllJobIds}
    />
  );
}
